import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const Parts = () => {
  const [parts, setParts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPart, setSelectedPart] = useState(null);
  const [partDetails, setPartDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [savingPart, setSavingPart] = useState(null);

  const fetchParts = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;

      const response = await api.get('/products/parts', { params });
      setParts(response.data.parts || []);
      setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
    } catch (error) {
      console.error('Error fetching parts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartDetails = async (productcode) => {
    try {
      const response = await api.get(`/products/parts/${encodeURIComponent(productcode)}`);
      setPartDetails(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching part details:', error);
    }
  };

  const saveExtraLength = async (productcode, value) => {
    setSavingPart(productcode);
    try {
      await api.put(`/products/parts/${encodeURIComponent(productcode)}/settings`, {
        extraLengthPercent: parseFloat(value) || 0
      });
    } catch (error) {
      console.error('Error saving extra length:', error);
    } finally {
      setSavingPart(null);
    }
  };

  const handleExtraLengthChange = (productcode, value) => {
    setParts(prev => prev.map(p =>
      p.productcode === productcode ? { ...p, extraLengthPercent: value } : p
    ));
  };

  const handleExtraLengthBlur = (productcode, value) => {
    const numValue = parseFloat(value) || 0;
    const original = parts.find(p => p.productcode === productcode);
    if (original && numValue !== (original._savedExtraLength ?? 0)) {
      saveExtraLength(productcode, numValue);
      setParts(prev => prev.map(p =>
        p.productcode === productcode ? { ...p, _savedExtraLength: numValue } : p
      ));
    }
  };

  useEffect(() => {
    fetchParts(1);
  }, []);

  // Track initial values for change detection
  useEffect(() => {
    setParts(prev => prev.map(p => ({
      ...p,
      _savedExtraLength: p._savedExtraLength ?? p.extraLengthPercent ?? 0
    })));
  }, [loading]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchParts(1);
  };

  const handlePageChange = (newPage) => {
    fetchParts(newPage);
  };

  const openPartDetails = (part) => {
    setSelectedPart(part);
    fetchPartDetails(part.productcode);
  };

  return (
    <div className="parts-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Materialen (Parts)</h1>
          <p className="text-muted">Overzicht van alle materialen en hun gebruik in producten</p>
        </div>
        <Link to="/products" className="btn btn-outline-secondary">
          <i className="bi bi-arrow-left me-2"></i>
          Terug naar Producten
        </Link>
      </div>

      {/* Search */}
      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch}>
            <div className="row g-3">
              <div className="col-md-10">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Zoek op materiaal code of naam..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <button type="submit" className="btn btn-primary w-100">
                  Zoeken
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Stats */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0">{pagination.total}</h3>
              <small className="text-muted">Unieke materialen</small>
            </div>
          </div>
        </div>
      </div>

      {/* Parts Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
            </div>
          ) : parts.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-box-seam fs-1 d-block mb-3"></i>
              <p>Geen materialen gevonden</p>
              <p className="small">Sync eerst de producten met Picqer om materialen te laden</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Materiaal Code</th>
                    <th>Naam</th>
                    <th>Type</th>
                    <th className="text-center">Aantal Producten</th>
                    <th>Extra lengte</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((part) => (
                    <tr key={part._id || part.productcode}>
                      <td>
                        <code className="text-primary">{part.productcode}</code>
                      </td>
                      <td className="fw-medium">{part.name}</td>
                      <td>
                        <span className="badge bg-secondary">
                          {part.type || 'normal'}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-info text-dark fs-6">
                          {part.productCount}
                        </span>
                      </td>
                      <td style={{ width: '130px' }}>
                        <div className="d-flex align-items-center gap-1">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: '70px' }}
                            value={part.extraLengthPercent ?? 0}
                            onChange={(e) => handleExtraLengthChange(part.productcode, e.target.value)}
                            onBlur={(e) => handleExtraLengthBlur(part.productcode, e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                            min="0"
                            max="100"
                            step="0.1"
                            disabled={savingPart === part.productcode}
                          />
                          <span className="text-muted small">%</span>
                          {savingPart === part.productcode && (
                            <span className="spinner-border spinner-border-sm text-primary"></span>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openPartDetails(part)}
                          title="Bekijk producten"
                        >
                          <i className="bi bi-eye me-1"></i>
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="card-footer">
            <nav>
              <ul className="pagination justify-content-center mb-0">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Vorige
                  </button>
                </li>
                <li className="page-item disabled">
                  <span className="page-link">
                    Pagina {pagination.page} van {pagination.totalPages}
                  </span>
                </li>
                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Volgende
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Part Details Modal */}
      {showModal && partDetails && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-box-seam me-2"></i>
                  {partDetails.part?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-4">
                  <div className="col-md-4">
                    <p className="mb-1 text-muted small">Materiaal Code</p>
                    <p className="fw-medium"><code>{partDetails.part?.productcode}</code></p>
                  </div>
                  <div className="col-md-2">
                    <p className="mb-1 text-muted small">Type</p>
                    <p><span className="badge bg-secondary">{partDetails.part?.type || 'normal'}</span></p>
                  </div>
                  <div className="col-md-3">
                    <p className="mb-1 text-muted small">Aantal Producten</p>
                    <p><span className="badge bg-info text-dark fs-6">{partDetails.productCount}</span></p>
                  </div>
                  <div className="col-md-3">
                    <p className="mb-1 text-muted small">Extra lengte</p>
                    <p className="fw-medium">{partDetails.part?.extraLengthPercent || 0}%</p>
                  </div>
                </div>

                <h6 className="mb-3">Producten met dit materiaal:</h6>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Product Code</th>
                        <th>Naam</th>
                        <th>Materiaal</th>
                        <th>Categorie</th>
                        <th className="text-end">Aantal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partDetails.products?.map((product) => (
                        <tr key={product._id}>
                          <td>
                            <Link to={`/products/${product._id}`}>
                              <code>{product.productCode}</code>
                            </Link>
                          </td>
                          <td>{product.name}</td>
                          <td>
                            <span className="badge bg-light text-dark">
                              {product.materialType || '-'}
                            </span>
                          </td>
                          <td>{product.category || '-'}</td>
                          <td className="text-end">{product.amount || 1}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Parts;
